/**
 * MQTT Admin Service
 *
 * This service extends the base MQTT service with admin-specific functionality
 * for the hybrid agent management model. It provides:
 *
 * - Secure, role-based MQTT connections for authorized admins
 * - Direct agent configuration and diagnostics via MQTT
 * - Audit logging of all MQTT admin actions
 * - Synchronization between MQTT changes and backend API
 */

import mqttService from './mqttService';
import * as agentApi from '../../api/agentApi';
import { getAuth } from 'firebase/auth';

// Track MQTT admin actions for audit logging
const actionLog = new Map();

/**
 * Initialize a secure MQTT admin connection
 * Only available to authorized users (admin, tenant_admin)
 *
 * @param {string} userRole - User's role (admin, tenant_admin)
 * @param {string} userTenant - User's tenant ID
 * @returns {Promise<Object>} MQTT client and admin capabilities
 */
export const initializeMqttAdminConnection = async (userRole, userTenant) => {
  try {
    // Get current user for audit logging
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      throw new Error('Authentication required for MQTT admin access');
    }

    if (!['admin', 'tenant_admin'].includes(userRole)) {
      throw new Error('Insufficient permissions for MQTT admin access');
    }

    return {
      client: null,
      isConnected: true,
      transport: 'backend',
      userRole,
      userTenant,
      userId: currentUser.uid,
      userEmail: currentUser.email,
    };
  } catch (error) {
    console.error('Error initializing MQTT admin connection:', error);
    throw error;
  }
};

/**
 * Apply configuration changes to an agent via MQTT
 * This is a direct, real-time update that doesn't persist to the backend
 *
 * @param {string} agentId - Agent ID
 * @param {Object} config - Configuration changes
 * @param {Object} userInfo - User information for audit logging
 * @returns {Promise<Object>} Result with action ID and timestamp
 */
export const applyConfigToAgent = async (agentId, config, userInfo) => {
  try {
    // Generate action ID for tracking and audit
    const actionId = `mqtt_cfg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    // Log the action before sending
    const actionDetails = {
      actionId,
      type: 'config',
      agentId,
      config,
      user: userInfo,
      timestamp: Date.now(),
      status: 'pending',
    };

    actionLog.set(actionId, actionDetails);

    // Send config via MQTT
    const configId = await mqttService.sendConfigToAgent(
      agentId,
      config.features || {},
      config.settings || {}
    );

    // Update action log with config ID
    actionDetails.configId = configId;
    actionDetails.status = 'sent';
    actionLog.set(actionId, actionDetails);

    return {
      actionId,
      configId,
      timestamp: actionDetails.timestamp,
      needsSync: true, // Flag indicating this change needs to be synced to backend
    };
  } catch (error) {
    console.error(`Error applying config to agent ${agentId} via MQTT:`, error);
    throw error;
  }
};

/**
 * Send a command to an agent via MQTT
 *
 * @param {string} agentId - Agent ID
 * @param {string} action - Command action (restart, diagnostics, etc.)
 * @param {Object} parameters - Command parameters
 * @param {Object} userInfo - User information for audit logging
 * @returns {Promise<Object>} Result with action ID and timestamp
 */
export const sendCommandToAgent = async (agentId, action, parameters, userInfo) => {
  try {
    // Generate action ID for tracking and audit
    const actionId = `mqtt_cmd_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    // Log the action before sending
    const actionDetails = {
      actionId,
      type: 'command',
      agentId,
      action,
      parameters,
      user: userInfo,
      timestamp: Date.now(),
      status: 'pending',
    };

    actionLog.set(actionId, actionDetails);

    // Send command via MQTT
    const commandId = await mqttService.sendCommandToAgent(agentId, action, parameters);

    // Update action log with command ID
    actionDetails.commandId = commandId;
    actionDetails.status = 'sent';
    actionLog.set(actionId, actionDetails);

    // Some commands may need backend sync (e.g., status changes)
    const needsSync = ['update_status', 'set_enabled', 'set_disabled'].includes(action);

    return {
      actionId,
      commandId,
      timestamp: actionDetails.timestamp,
      needsSync,
    };
  } catch (error) {
    console.error(`Error sending command to agent ${agentId} via MQTT:`, error);
    throw error;
  }
};

/**
 * Sync MQTT changes to the backend via REST API
 * This ensures the backend remains the source of truth
 *
 * @param {string} agentId - Agent ID
 * @param {Object} changes - Changes to sync
 * @param {string} actionId - Original MQTT action ID for audit trail
 * @param {Object} userInfo - User information for audit logging
 * @returns {Promise<Object>} Sync result
 */
export const syncChangesToBackend = async (agentId, changes, actionId, userInfo) => {
  try {
    // Log the sync attempt
    console.log(`Syncing MQTT changes for agent ${agentId} to backend:`, changes);

    // Get the original action details
    const actionDetails = actionLog.get(actionId) || {
      type: 'unknown',
      timestamp: Date.now(),
    };

    // Create sync action in log
    const syncActionId = `sync_${actionId}`;
    const syncDetails = {
      actionId: syncActionId,
      originalActionId: actionId,
      type: 'sync',
      agentId,
      changes,
      user: userInfo,
      timestamp: Date.now(),
      status: 'pending',
    };

    actionLog.set(syncActionId, syncDetails);

    // Perform the appropriate API call based on the change type
    let result;

    if (actionDetails.type === 'config') {
      // Update agent via API
      result = await agentApi.updateAgent(agentId, changes);
      syncDetails.status = 'completed';
    } else if (actionDetails.type === 'command') {
      // Handle specific command types that need API updates
      if (actionDetails.action === 'update_status') {
        result = await agentApi.updateAgent(agentId, { status: changes.status });
        syncDetails.status = 'completed';
      } else if (['set_enabled', 'set_disabled'].includes(actionDetails.action)) {
        result = await agentApi.toggleAgentStatus(agentId, actionDetails.action === 'set_enabled');
        syncDetails.status = 'completed';
      } else {
        // No sync needed for other commands
        syncDetails.status = 'skipped';
        result = { message: 'No backend sync required for this command' };
      }
    }

    // Update sync action log
    syncDetails.result = result;
    actionLog.set(syncActionId, syncDetails);

    return {
      syncId: syncActionId,
      success: true,
      timestamp: syncDetails.timestamp,
      result,
    };
  } catch (error) {
    console.error(`Error syncing MQTT changes for agent ${agentId} to backend:`, error);

    // Log the sync failure
    const syncActionId = `sync_${actionId}`;
    const syncDetails = actionLog.get(syncActionId) || {
      actionId: syncActionId,
      originalActionId: actionId,
      type: 'sync',
      agentId,
      changes,
      user: userInfo,
      timestamp: Date.now(),
    };

    syncDetails.status = 'failed';
    syncDetails.error = error.message;
    actionLog.set(syncActionId, syncDetails);

    throw error;
  }
};

/**
 * Get audit log entries for MQTT admin actions
 *
 * @param {Object} filters - Optional filters (agentId, actionType, etc.)
 * @returns {Array<Object>} Filtered audit log entries
 */
export const getMqttAuditLog = (filters = {}) => {
  let entries = Array.from(actionLog.values());

  // Apply filters if provided
  if (filters.agentId) {
    entries = entries.filter(entry => entry.agentId === filters.agentId);
  }

  if (filters.actionType) {
    entries = entries.filter(entry => entry.type === filters.actionType);
  }

  if (filters.userId) {
    entries = entries.filter(entry => entry.user?.userId === filters.userId);
  }

  if (filters.startTime) {
    entries = entries.filter(entry => entry.timestamp >= filters.startTime);
  }

  if (filters.endTime) {
    entries = entries.filter(entry => entry.timestamp <= filters.endTime);
  }

  // Sort by timestamp (newest first)
  return entries.sort((a, b) => b.timestamp - a.timestamp);
};

/**
 * Subscribe to agent status updates for real-time monitoring
 *
 * @param {string} agentId - Agent ID
 * @param {Function} callback - Callback function for status updates
 * @returns {Promise<Function>} Unsubscribe function
 */
export const monitorAgentStatus = async (agentId, callback) => {
  return mqttService.subscribeToAgentStatus(agentId, callback);
};

/**
 * Subscribe to agent result messages for command responses
 *
 * @param {string} agentId - Agent ID
 * @param {Function} callback - Callback function for result messages
 * @returns {Promise<Function>} Unsubscribe function
 */
export const monitorAgentResults = async (agentId, callback) => {
  return mqttService.subscribeToAgentResults(agentId, callback);
};

export default {
  initializeMqttAdminConnection,
  applyConfigToAgent,
  sendCommandToAgent,
  syncChangesToBackend,
  getMqttAuditLog,
  monitorAgentStatus,
  monitorAgentResults,
};
